/**
 * The small readings a bar is assembled from: a rest of a given length, an event, a voice,
 * a dynamic, a tempo, a clef.
 *
 * Separated from the walk over `<measure>` so that the walk reads as the cursor logic it
 * is, and so that each of these can be reasoned about on its own -- `restsFor` in
 * particular, which is the only place in the importer that invents notation the file did
 * not contain, and does so only to fill time the file left empty.
 */

import { NOTE_TYPES, plain, ticksOf } from "../document/duration";
import type { Duration } from "../document/duration";
import { createId } from "../document/ids";
import type { Clef, DynamicMark, MusicEvent, Voice } from "../document/types";
import { attr, child, childrenNamed, numberOf, textOf } from "../xml/read";
import type { XmlElement } from "../xml/parse";
import type { NoteRead } from "./import-notes";

/** A tempo as the document states it, before it is put on the global timeline. */
export interface TempoRead {
  readonly bpm: number;
  readonly unit: Duration;
  readonly text: string | null;
}

/** One event and the tick of the bar it was written at. */
export interface Placed {
  readonly onset: number;
  readonly ticks: number;
  /** The staff the file drew it on, zero-based, which need not be the voice's own. */
  readonly staff: number;
  event: MusicEvent;
}

/** One MusicXML voice while it is being collected. */
export interface VoiceBuild {
  staff: number;
  readonly placed: Placed[];
}

/** Note values usable as a rest, longest first, for filling a gap the file left. */
const REST_SHAPES: readonly Duration[] = NOTE_TYPES.flatMap((type) => [
  { type, dots: 2, tuplet: null },
  { type, dots: 1, tuplet: null },
  { type, dots: 0, tuplet: null },
]).sort((a, b) => ticksOf(b) - ticksOf(a));

/** Rests that together last `ticks`, largest first. */
export function restsFor(ticks: number): MusicEvent[] {
  const events: MusicEvent[] = [];
  let left = ticks;
  while (left > 0 && events.length < 32) {
    const shape = REST_SHAPES.find((candidate) => ticksOf(candidate) <= left);
    if (shape === undefined) break;
    events.push({
      id: createId("event"),
      kind: "rest",
      duration: shape,
      wholeMeasure: false,
      fermata: null,
      cue: false,
      offset: null,
      staff: null,
    });
    left -= ticksOf(shape);
  }
  return events;
}

const DYNAMICS: readonly DynamicMark[] = [
  "pppp",
  "ppp",
  "pp",
  "p",
  "mp",
  "mf",
  "f",
  "ff",
  "fff",
  "ffff",
  "sf",
  "sfz",
  "fp",
  "rf",
  "rfz",
];

export function eventOf(read: NoteRead): MusicEvent {
  if (read.rest) {
    return {
      id: createId("event"),
      kind: "rest",
      duration: read.wholeMeasure ? plain("whole") : read.duration,
      wholeMeasure: read.wholeMeasure,
      fermata: read.fermata,
      cue: read.cue,
      offset: read.offset,
      staff: null,
    };
  }
  return {
    id: createId("event"),
    kind: "note",
    duration: read.duration,
    pitches: read.pitch === null ? [] : [read.pitch],
    stem: read.stem,
    beam: read.beam,
    articulations: read.articulations,
    ornaments: read.ornaments,
    arpeggio: read.arpeggio,
    tremolo: read.tremolo,
    fermata: read.fermata,
    cue: read.cue,
    offset: read.offset,
    staff: null,
    grace: read.grace,
    lyrics: read.lyrics,
  };
}

/**
 * Which staff a voice lives on, given where each of its notes was drawn.
 *
 * The staff most of the voice's notes are on, because that is what a cross-staff passage
 * means: the voice is the hand, and the notes that left the hand's own staff are the ones
 * reaching over. Counting rather than taking the first note matters for a bar that opens
 * with the reach -- an arpeggio starting low in the left hand's staff is still the right
 * hand's voice -- and a voice written wholly on one staff is unaffected either way.
 */
function homeStaff(placed: readonly Placed[], fallback: number): number {
  const counts = new Map<number, number>();
  for (const one of placed) counts.set(one.staff, (counts.get(one.staff) ?? 0) + 1);
  let home = fallback;
  let most = 0;
  for (const [staff, count] of [...counts].sort((a, b) => a[0] - b[0])) {
    if (count <= most) continue;
    home = staff;
    most = count;
  }
  return home;
}

/** Turn the recorded onsets of one voice back into a gap-free list of events. */
export function buildVoice(build: VoiceBuild): Voice {
  const ordered = [...build.placed].sort((a, b) => a.onset - b.onset);
  const staff = homeStaff(build.placed, build.staff);
  const events: MusicEvent[] = [];
  let at = 0;
  for (const placed of ordered) {
    if (placed.onset > at) events.push(...restsFor(placed.onset - at));
    // A note the file drew on another staff of the part is a cross-staff note, and stays
    // one: the voice keeps it, the other staff draws it. See `NoteEvent.staff`.
    events.push(placed.staff === staff ? placed.event : { ...placed.event, staff: placed.staff });
    at = Math.max(at, placed.onset + placed.ticks);
  }
  return { id: createId("voice"), staff, events, ossia: false };
}

export function dynamicOf(element: XmlElement): DynamicMark | null {
  for (const candidate of element.children) {
    if (DYNAMICS.includes(candidate.name as DynamicMark)) return candidate.name as DynamicMark;
  }
  return null;
}

export function tempoOf(direction: XmlElement): TempoRead | null {
  const types = childrenNamed(direction, "direction-type");
  const metronome =
    types.map((type) => child(type, "metronome")).find((node) => node !== null) ?? null;
  const words = types.map((type) => textOf(type, "words")).find((text) => text !== null) ?? null;
  const sound = child(direction, "sound");
  const perMinute = metronome === null ? null : numberOf(metronome, "per-minute", 0);

  if (metronome !== null && perMinute !== null && perMinute > 0) {
    const unit = textOf(metronome, "beat-unit") ?? "quarter";
    const dots = childrenNamed(metronome, "beat-unit-dot").length;
    const type = NOTE_TYPES.includes(unit as (typeof NOTE_TYPES)[number]) ? unit : "quarter";
    return {
      bpm: perMinute,
      unit: { type: type as (typeof NOTE_TYPES)[number], dots, tuplet: null },
      text: words,
    };
  }
  const sounded = sound === null ? 0 : Number(attr(sound, "tempo") ?? 0);
  if (Number.isFinite(sounded) && sounded > 0) {
    return { bpm: Math.round(sounded), unit: plain("quarter"), text: words };
  }
  return null;
}

export function clefOf(element: XmlElement): Clef {
  const sign = textOf(element, "sign") ?? "G";
  const allowed = ["G", "F", "C", "percussion", "TAB"];
  return {
    sign: (allowed.includes(sign) ? sign : "G") as Clef["sign"],
    line: numberOf(element, "line", sign === "F" ? 4 : sign === "C" ? 3 : 2),
    octaveChange: numberOf(element, "clef-octave-change", 0),
  };
}
